import { supabase } from '../lib/supabase';
import { notificationService } from './notificationService';

/**
 * Error-surfacing pattern: see canonical doc-comment at the top of
 * src/services/clientService.js. Preserves the real Postgrest/Storage
 * error object on the `{ data, error }` return so page-layer callers
 * can branch on err.code (e.g. PGRST301 for RLS) instead of relying on
 * a stripped `{ message }` shell.
 */
function surfaceError(scope, error) {
  console.error(`[siteInductionsService] ${scope} failed:`, {
    code: error?.code,
    message: error?.message,
    details: error?.details,
    hint: error?.hint,
  });
  return error;
}

export const siteInductionsService = {
  /**
   * Upload a site induction file to Supabase Storage and create the DB record.
   */
  async uploadInduction(title, siteId, file, uploadedBy, companyId) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${companyId}/${siteId}/${Date.now()}_${file?.name?.replace(/[^a-zA-Z0-9._-]/g, '_')}`;

      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('site-inductions')
        ?.upload(fileName, file, { upsert: false });

      if (uploadError) throw uploadError;

      const filePath = uploadData?.path;

      const { data, error } = await supabase
        ?.from('site_inductions')
        ?.insert({
          company_id: companyId,
          site_id: siteId,
          title,
          file_path: filePath,
          uploaded_by: uploadedBy,
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  /**
   * Assign an induction to multiple workers.
   */
  async assignInductionToWorkers(inductionId, workerUserIds) {
    try {
      if (!workerUserIds?.length) return { data: [], error: null };

      const rows = workerUserIds?.map((wid) => ({
        induction_id: inductionId,
        worker_user_id: wid,
      }));

      const { data, error } = await supabase
        ?.from('site_induction_assignments')
        ?.upsert(rows, { onConflict: 'induction_id,worker_user_id' })
        ?.select();

      if (error) throw error;

      // Fetch induction title for notification message
      const { data: induction } = await supabase?.from('site_inductions')?.select('title')?.eq('id', inductionId)?.maybeSingle();
      const title = induction?.title || 'Site Induction';

      // Notify each assigned worker
      for (const wid of workerUserIds) {
        notificationService?.notifyInductionAssigned(inductionId, wid, title);
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  /**
   * Worker completes an induction.
   */
  async completeInduction(inductionId, workerUserId) {
    try {
      const { data, error } = await supabase
        ?.from('site_induction_completions')
        ?.upsert(
          { induction_id: inductionId, worker_user_id: workerUserId },
          { onConflict: 'induction_id,worker_user_id' }
        )
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  /**
   * Get all inductions for a site (admin/site_manager).
   *
   * NSherjFh PR 9: default-filters soft-deleted inductions. Pass
   * `{ includeDeleted: true }` from the site-inductions-management
   * 'View deleted' tab. Mirrors PR 2-8 opt-in shape.
   */
  async getInductionsForSite(siteId, companyId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('site_inductions')
        ?.select('*, uploader:uploaded_by(id, full_name)')
        ?.eq('site_id', siteId)
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  /**
   * Get all inductions for a company (admin view).
   */
  async getInductionsForCompany(companyId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('site_inductions')
        ?.select('*, site:site_id(id, site_name), uploader:uploaded_by(id, full_name)')
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  /**
   * Get all inductions assigned to a worker.
   *
   * NSherjFh PR 9: pull `deleted_at` on the embedded induction so we
   * can drop assignments to soft-deleted parents below. Workers
   * should never see a soft-deleted induction in their acknowledgement
   * queue. The assignment + any past completion rows stay in the DB
   * as historical fact; we just hide them from the active view. Same
   * pattern as PR 3 rams getRamsForWorker.
   */
  async getInductionsForWorker(workerUserId) {
    try {
      const { data, error } = await supabase
        ?.from('site_induction_assignments')
        ?.select('*, induction:induction_id(id, title, file_path, created_at, deleted_at, site:site_id(id, site_name))')
        ?.eq('worker_user_id', workerUserId)
        ?.order('assigned_at', { ascending: false });

      if (error) throw error;

      // Drop assignments whose parent induction is soft-deleted.
      // Keeping the assignment row is correct (it's history) but the
      // worker shouldn't be asked to acknowledge a removed induction.
      const filtered = (data || [])?.filter((a) => a?.induction && !a?.induction?.deleted_at);
      return { data: filtered, error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  /**
   * Get completion status for a specific worker + induction.
   */
  async getWorkerCompletionStatus(inductionId, workerUserId) {
    try {
      const { data, error } = await supabase
        ?.from('site_induction_completions')
        ?.select('*')
        ?.eq('induction_id', inductionId)
        ?.eq('worker_user_id', workerUserId)
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  /**
   * Get all completions for an induction.
   */
  async getCompletions(inductionId) {
    try {
      const { data, error } = await supabase
        ?.from('site_induction_completions')
        ?.select('*, worker:worker_user_id(id, full_name)')
        ?.eq('induction_id', inductionId)
        ?.order('completed_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  /**
   * Get all assignments + completion status for all inductions in a company.
   */
  async getAllAssignmentsForCompany(companyId) {
    try {
      const { data: docs, error: docsErr } = await supabase
        ?.from('site_inductions')
        ?.select('id, title, site:site_id(id, site_name)')
        ?.eq('company_id', companyId)
        // NSherjFh PR 9: the acknowledgement tracking table on site-
        // inductions-management is an active view — assignments to
        // soft-deleted inductions are hidden from the live grid
        // (their rows stay in site_induction_assignments and
        // site_induction_completions as historical fact, but the
        // parent doc is gated out here so the grid + stats roll-up
        // don't count them). Mirrors PR 3 rams getAllAssignments
        // ForCompany.
        ?.is('deleted_at', null);

      if (docsErr) throw docsErr;
      if (!docs?.length) return { data: [], error: null };

      const docIds = docs?.map((d) => d?.id);

      const { data: assignments, error: aErr } = await supabase
        ?.from('site_induction_assignments')
        ?.select('*, worker:worker_user_id(id, full_name)')
        ?.in('induction_id', docIds);

      if (aErr) throw aErr;

      const { data: completions, error: cErr } = await supabase
        ?.from('site_induction_completions')
        ?.select('induction_id, worker_user_id, completed_at')
        ?.in('induction_id', docIds);

      if (cErr) throw cErr;

      const completionMap = {};
      completions?.forEach((c) => {
        completionMap[`${c?.induction_id}_${c?.worker_user_id}`] = c?.completed_at;
      });

      const docMap = {};
      docs?.forEach((d) => { docMap[d?.id] = d; });

      const merged = assignments?.map((a) => ({
        ...a,
        induction: docMap?.[a?.induction_id],
        completed: !!completionMap?.[`${a?.induction_id}_${a?.worker_user_id}`],
        completed_at: completionMap?.[`${a?.induction_id}_${a?.worker_user_id}`] || null,
      }));

      return { data: merged || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  /**
   * Get workers assigned to a site.
   */
  async getWorkersForSite(siteId) {
    try {
      // Two-step fetch. There is no `site_assignments` table — the real
      // table is `worker_site_assignments`, keyed by `user_id` (FK ->
      // user_profiles). The old query named a non-existent table AND
      // embedded via a non-existent `worker_user_id` column, so PostgREST
      // failed (PGRST205) and the Assign-to-Workers panel silently showed
      // zero workers. Fetch the assignment user_ids first, then look up
      // the profiles — avoids any relationship-embed surprise. Mirrors
      // ramsService / toolboxTalksService.getWorkersForSite. See [EnwlXavr].
      const { data: assignments, error: assignError } = await supabase
        ?.from('worker_site_assignments')
        ?.select('user_id')
        ?.eq('site_id', siteId);
      if (assignError) throw assignError;
      const userIds = (assignments || [])?.map((a) => a?.user_id)?.filter(Boolean);
      if (!userIds?.length) return { data: [], error: null };
      const { data: profiles, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name')
        ?.in('id', userIds);
      if (profileError) throw profileError;
      return { data: profiles || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  /**
   * NSherjFh PR 9 — recoverable soft-delete on the induction document.
   *
   * Built from scratch (no existing delete method or UI). Mirrors
   * PR 3 rams deleteRamsDocument exactly:
   *   - Stamps deleted_at + deleted_by on the row.
   *   - Leaves the storage file in `site-inductions` intact so
   *     restore surfaces both row and file.
   *   - Idempotent via `.is('deleted_at', null)`: a second click is
   *     a no-op and preserves the original deleter attribution.
   *   - Existing site_induction_assignments and site_induction_
   *     completions rows are NOT deleted — they're historical
   *     compliance audit trail (workers' sign-offs are facts
   *     regardless of whether the parent is later deleted). The
   *     active management tracking grid filters them out via
   *     getAllAssignmentsForCompany's parent-doc filter; per-doc
   *     completion history (getCompletions, getWorkerCompletion
   *     Status) intentionally does NOT filter, so audits stay
   *     complete.
   *
   * RLS: covered by the existing inductions_admin_site_manager_all
   * FOR ALL policy (USING admin/SM + company_id; no deleted_at
   * predicate, so the UPDATE permission applies to both soft-delete
   * and restore).
   */
  async softDeleteInduction(inductionId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        ?.from('site_inductions')
        ?.update({
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id,
        })
        ?.eq('id', inductionId)
        ?.is('deleted_at', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('softDeleteInduction', error) };
    }
  },

  /**
   * Inverse of softDeleteInduction. Existing assignments and
   * completions remain unchanged across the soft-delete round trip
   * (per the template/acknowledgement split — they're historical
   * facts), so restore is a single UPDATE on the parent row.
   */
  async restoreInduction(inductionId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        ?.from('site_inductions')
        ?.update({
          deleted_at: null,
          deleted_by: null,
        })
        ?.eq('id', inductionId)
        ?.not('deleted_at', 'is', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('restoreInduction', error) };
    }
  },

  /**
   * Get a signed URL for an induction file.
   *
   * Mirrors ramsService.getSignedUrl from [gwwbt45U]: passes the `download`
   * option so non-renderable MIMEs (.docx etc.) come back as
   * `Content-Disposition: attachment` and behave like clean downloads
   * instead of half-rendering tabs after the popup-blocker fix in
   * InductionCard. The site-inductions bucket also accepts video MIMEs;
   * we still force download for consistency, since the worker view is
   * mobile-first and large videos stream poorly on cellular — flag if
   * inline streaming becomes a requirement.
   */
  async getSignedUrl(filePath, downloadFilename) {
    try {
      if (!filePath) {
        const err = new Error('No file path provided');
        err.code = 'NO_FILE_PATH';
        throw err;
      }
      const options = downloadFilename
        ? { download: downloadFilename }
        : { download: true };
      const { data, error } = await supabase?.storage
        ?.from('site-inductions')
        ?.createSignedUrl(filePath, 3600, options);

      if (error) throw error;
      return { url: data?.signedUrl, error: null };
    } catch (error) {
      // Preserve the real error object so the page-layer caller can
      // branch on err.code (e.g. PGRST301 for permission). The previous
      // `{ message: error?.message }` shape stripped code/details/hint.
      return { url: null, error: surfaceError('getSignedUrl', error) };
    }
  },
};
